/**
 * Groupings landing — table of dimensions; open → FamilyBoard (WSRADM-0032).
 * Rename/Delete/Create on tenant rows save immediately via putDimensions. No draft.
 */
import { useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { putDimensions } from '@/api/dimensions/dimensionsApi';
import { slugFromDisplayName } from '@/api/dimensions/slug';
import type { DimensionStructure, DimensionStructureSaved } from '@/api/dimensions/types';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import type { ResourceStatus } from '@/hooks/useResource';
import { Icon } from '@/theme';
import {
  STRUCTURE_SAVE_ERROR_FALLBACK,
  cloneStructure,
  mapSaveError,
  maxSortOrder,
  toPutBody,
  type StructureSaveErrorKey,
} from './structureModel';

const NAME_MAX = 60;

export interface GroupingsLandingProps {
  structure: DimensionStructure | null;
  structureStatus: ResourceStatus;
  /** The structure load error, shown with copyable details. */
  structureError?: unknown;
  refetchStructure: () => void;
  onStructureSaved: (next: DimensionStructureSaved) => void;
  onOpen: (slug: string) => void;
  /** Page-derived `details.code === 'UNKNOWN_TENANT'` — suppress load error chrome. */
  unknownTenant?: boolean;
}

export function GroupingsLanding({
  structure,
  structureStatus,
  structureError,
  refetchStructure,
  onStructureSaved,
  onOpen,
  unknownTenant = false,
}: GroupingsLandingProps) {
  const { t } = useCopy();
  const headingId = useId();
  const [renamingSlug, setRenamingSlug] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteSlug, setConfirmDeleteSlug] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createValue, setCreateValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<StructureSaveErrorKey | null>(null);
  // The caught error behind `saveError`, for the copyable details.
  const [saveFailure, setSaveFailure] = useState<unknown>(null);

  const takenSlugs = useMemo(
    () => new Set(structure?.dimensions.map((d) => d.slug) ?? []),
    [structure],
  );

  const createTrimmed = createValue.trim();
  const createBaseSlug =
    createTrimmed.length === 0 ? '' : slugFromDisplayName(createTrimmed, []);
  const createDuplicate =
    createTrimmed.length > 0 && createBaseSlug.length > 0 && takenSlugs.has(createBaseSlug);
  const createValid =
    createTrimmed.length > 0 && createTrimmed.length <= NAME_MAX && !createDuplicate;

  async function commitRename(slug: string, displayName: string) {
    if (structure === null) return;
    const trimmed = displayName.trim();
    const current = structure.dimensions.find((d) => d.slug === slug);
    setRenamingSlug(null);
    if (current === undefined || trimmed === '' || trimmed === current.displayName) return;
    setSaving(true);
    setSaveError(null);
    setSaveFailure(null);
    try {
      const next: DimensionStructure = {
        dimensions: structure.dimensions.map((d) =>
          d.slug === slug ? { ...d, displayName: trimmed } : d,
        ),
      };
      const response = await putDimensions(toPutBody(next));
      onStructureSaved(response);
      setSaving(false);
    } catch (err) {
      setSaving(false);
      setSaveError(mapSaveError(err));
      setSaveFailure(err);
    }
  }

  async function confirmDelete(slug: string) {
    if (structure === null) return;
    setSaving(true);
    setSaveError(null);
    setSaveFailure(null);
    try {
      const next: DimensionStructure = {
        dimensions: structure.dimensions.filter((d) => d.slug !== slug),
      };
      const response = await putDimensions(toPutBody(next));
      onStructureSaved(response);
      setConfirmDeleteSlug(null);
      setSaving(false);
    } catch (err) {
      setSaving(false);
      setSaveError(mapSaveError(err));
      setSaveFailure(err);
    }
  }

  async function commitCreate() {
    if (structure === null || !createValid) return;
    const trimmed = createTrimmed;
    const next = cloneStructure(structure);
    const taken = new Set(next.dimensions.map((d) => d.slug));
    const slug = slugFromDisplayName(trimmed, taken);
    if (taken.has(slug)) return;
    next.dimensions.push({
      slug,
      displayName: trimmed,
      sortOrder: maxSortOrder(next.dimensions) + 1,
      source: 'TENANT',
      readOnly: false,
      families: [],
    });
    setSaving(true);
    setSaveError(null);
    setSaveFailure(null);
    try {
      const response = await putDimensions(toPutBody(next));
      onStructureSaved(response);
      setCreating(false);
      setCreateValue('');
      setSaving(false);
    } catch (err) {
      setSaving(false);
      setSaveError(mapSaveError(err));
      setSaveFailure(err);
    }
  }

  if (structureStatus === 'loading' && structure === null) {
    return (
      <Box component="section" aria-labelledby={headingId}>
        <Skeleton variant="rounded" height={48} role="status" sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={48} role="status" sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={48} role="status" />
      </Box>
    );
  }

  if (structureStatus === 'error' && !unknownTenant) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetchStructure}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      >
        {t('config.classification.loadError', 'Could not load the classification structure.')}
        <ErrorDetails
          intent="read"
          error={structureError}
          headline={t('config.classification.loadError', 'Could not load the classification structure.')}
        />
      </Alert>
    );
  }

  if (structure === null) return null;

  const tenantDims = structure.dimensions.filter((d) => d.readOnly !== true);
  const confirmTarget =
    confirmDeleteSlug === null
      ? null
      : (structure.dimensions.find((d) => d.slug === confirmDeleteSlug) ?? null);

  return (
    <Box component="section" aria-labelledby={headingId}>
      <Typography id={headingId} sx={{ mb: 2 }} color="text.secondary">
        {t(
          'config.groupings.lede',
          'Groupings define the columns on your revenue report. Open one to assign products to families.',
        )}
      </Typography>

      {saveError !== null ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(`config.classification.err.${saveError}`, STRUCTURE_SAVE_ERROR_FALLBACK[saveError])}
          <ErrorDetails
            error={saveFailure}
            headline={t(
              `config.classification.err.${saveError}`,
              STRUCTURE_SAVE_ERROR_FALLBACK[saveError],
            )}
          />
        </Alert>
      ) : null}

      <Stack direction="row" sx={{ mb: 1.5, justifyContent: 'flex-end' }}>
        {!creating ? (
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => {
              setCreating(true);
              setCreateValue('');
              setRenamingSlug(null);
              setConfirmDeleteSlug(null);
              setSaveError(null);
              setSaveFailure(null);
            }}
          >
            {t('config.groupings.new', 'New grouping')}
          </Button>
        ) : null}
      </Stack>

      {creating ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 2, alignItems: { sm: 'flex-start' } }}
          data-testid="new-grouping-editor"
        >
          <Box sx={{ flex: 1 }}>
            <TextField
              size="small"
              fullWidth
              /* Spec Step 1 requires autofocus on the new-grouping name field. */
              // eslint-disable-next-line jsx-a11y/no-autofocus -- create UX
              autoFocus
              value={createValue}
              disabled={saving}
              onChange={(e) => setCreateValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void commitCreate();
                }
                if (e.key === 'Escape') {
                  setCreating(false);
                  setCreateValue('');
                }
              }}
              label={t('config.groupings.newName', 'Grouping name')}
              error={createDuplicate}
              helperText={
                createDuplicate
                  ? t('config.groupings.err_duplicate', 'A grouping with that name already exists.')
                  : undefined
              }
              slotProps={{
                htmlInput: {
                  maxLength: NAME_MAX,
                  'aria-label': t('config.groupings.newName', 'Grouping name'),
                },
              }}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              disabled={saving || !createValid}
              onClick={() => void commitCreate()}
            >
              {t('config.groupings.create', 'Create')}
            </Button>
            <Button
              disabled={saving}
              onClick={() => {
                setCreating(false);
                setCreateValue('');
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </Stack>
        </Stack>
      ) : null}

      {tenantDims.length === 0 ? (
        <Typography sx={{ mb: 2 }}>
          {t(
            'config.groupings.emptyHint',
            'No grouping of your own yet. Create one to define the columns on your revenue report.',
          )}
        </Typography>
      ) : null}

      <Table size="small" aria-label={t('config.groupings.col_grouping', 'Grouping')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('config.groupings.col_grouping', 'Grouping')}</TableCell>
            <TableCell>{t('config.groupings.col_families', 'Families')}</TableCell>
            <TableCell />
            <TableCell align="right">{t('config.groupings.open', 'Open')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {structure.dimensions.map((dimension) => {
            const locked = dimension.readOnly === true;
            const isRenaming = renamingSlug === dimension.slug;
            const isConfirming = confirmDeleteSlug === dimension.slug;
            return (
              <TableRow key={dimension.slug} data-testid={`grouping-${dimension.slug}`}>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      {isRenaming && !locked ? (
                        <TextField
                          size="small"
                          value={renameValue}
                          disabled={saving}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onBlur={() => void commitRename(dimension.slug, renameValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitRename(dimension.slug, renameValue);
                            }
                            if (e.key === 'Escape') setRenamingSlug(null);
                          }}
                          slotProps={{
                            htmlInput: {
                              'aria-label': t('config.groupings.rename', 'Rename'),
                            },
                          }}
                        />
                      ) : (
                        <Button
                          variant="text"
                          onClick={() => onOpen(dimension.slug)}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0 }}
                        >
                          {dimension.displayName}
                        </Button>
                      )}
                      {locked ? (
                        <Chip
                          size="small"
                          icon={<Icon name="lock" />}
                          label={t('config.classification.locked', 'Locked')}
                        />
                      ) : null}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {locked
                        ? t(
                            'config.groupings.wstudioNote',
                            'From your point-of-sale — read-only here.',
                          )
                        : t(
                            'config.groupings.revenueNote',
                            'Columns on the Revenue Stats report.',
                          )}
                    </Typography>
                    {isConfirming && confirmTarget !== null ? (
                      <Box
                        data-testid={`confirm-del-${dimension.slug}`}
                        sx={{
                          mt: 1,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {t(
                            'config.groupings.confirmDelGrouping',
                            'Deleting removes these families and their allocations:',
                          )}
                        </Typography>
                        {confirmTarget.families.map((family) => (
                          <Typography key={family.slug} variant="body2">
                            {`${family.displayName} — ${family.productCount} products`}
                          </Typography>
                        ))}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            disabled={saving}
                            onClick={() => setConfirmDeleteSlug(null)}
                          >
                            {t('common.cancel', 'Cancel')}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={saving}
                            onClick={() => void confirmDelete(dimension.slug)}
                          >
                            {t('config.groupings.del', 'Delete')}
                          </Button>
                        </Stack>
                      </Box>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  {t('config.groupings.nFamilies', '{{count}} families', {
                    count: dimension.families.length,
                  })}
                </TableCell>
                <TableCell>
                  {!locked ? (
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        disabled={saving || isConfirming}
                        onClick={() => {
                          setRenameValue(dimension.displayName);
                          setRenamingSlug(dimension.slug);
                          setConfirmDeleteSlug(null);
                          setCreating(false);
                        }}
                      >
                        {t('config.groupings.rename', 'Rename')}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={saving || isRenaming}
                        onClick={() => {
                          setConfirmDeleteSlug(dimension.slug);
                          setRenamingSlug(null);
                          setCreating(false);
                        }}
                      >
                        {t('config.groupings.del', 'Delete')}
                      </Button>
                    </Stack>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={saving}
                    onClick={() => onOpen(dimension.slug)}
                  >
                    {t('config.groupings.open', 'Open')}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
